// ChatPage — AI 채팅. SSE 스트리밍 + 도구 루프(문서생성·코워크·사내데이터·웹검색) + 대화목록/보관함/프로젝트.
// frontend/src/pages/ChatPage.jsx
// 상태·로직은 pages/chat/hooks/* 커스텀 훅으로 분리(#133 순수 리팩터). 이 파일은 훅 조합 + 화면 구성만 담당.
// 공유 핵심상태(대화·메시지·프로젝트선택·코워크)는 useChatCore가 보유해 도메인 훅 간 순환 의존을 끊는다.
import { useEffect, useRef, useState } from 'react';
import AppShell from '../components/AppShell';
import Announcements from '../components/Announcements';
import ConversationList from './chat/ConversationList';
import ChatInput from './chat/ChatInput';
import MessageList from './chat/MessageList';
import ChatModals from './chat/ChatModals';
import WorkspacePanel from './chat/WorkspacePanel';
import { authAPI } from '../api/client';
import { syncFontFromUser } from '../utils/fontPrefs';
import useChatCore from './chat/hooks/useChatCore';
import useChatUI from './chat/hooks/useChatUI';
import useChatFeatures from './chat/hooks/useChatFeatures';
import useAttachments from './chat/hooks/useAttachments';
import useNotifications from './chat/hooks/useNotifications';
import useChatLimits from './chat/hooks/useChatLimits';
import useChatExport from './chat/hooks/useChatExport';
import useWorkspace from './chat/hooks/useWorkspace';
import useChatMessage from './chat/hooks/useChatMessage';
import useConversation from './chat/hooks/useConversation';
import useProjects from './chat/hooks/useProjects';
import usePrompts from './chat/hooks/usePrompts';
import { IconAlertTriangle, IconSparkles } from '@tabler/icons-react';
import { COLORS } from '../utils/uiStyles';
import { tabbarCalc } from '../lib/tabbarMetrics';

export default function ChatPage() {
  const dragDepth = useRef(0);
  const convParamRef = useRef('');
  const projectParamRef = useRef('');
  const [chatError, setChatError] = useState(null);
  // ── 상태 그룹 범례(#133) — 53개 useState를 책임별 훅으로 분리. 각 훅이 담는 상태/로직:
  //   useChatCore       : 공유 핵심상태(대화목록·현재대화·messages·프로젝트선택·코워크) — 순환의존 차단용 단일 보유처
  //   useChatUI         : 레이아웃·스크롤·드로어·저장메뉴 등 화면 로컬 UI
  //   useChatFeatures   : 토글(웹검색·사내데이터·RAG)·모델·권한(db/drive/lan)·RAG출처 + me/models/사용량 로드
  //   useAttachments    : 첨부 파일(업로드·붙여넣기·D&D·다운로드) + 드라이브 폴더 첨부
  //   useNotifications  : 알림 큐(30초 폴링)·읽음
  //   useChatLimits     : 토큰/웹검색 한도 모달·상향요청
  //   useChatExport     : 메시지/대화 저장·내보내기(TXT/MD/PDF/DOCX)·렌더 헬퍼
  //   useWorkspace      : 코워크 작업공간 파일 + 코워크 토글
  //   useChatMessage    : 메시지 전송/재생성 SSE 스트리밍 엔진(포그라운드/백그라운드 분리·rAF 타이핑·중지)
  //   useConversation   : 대화목록 필터/검색/제목편집 + 열기·새대화·삭제·즐겨찾기·보관·태그
  //   useProjects       : 프로젝트 CRUD·선택·설정모달·지식문서
  //   usePrompts        : 프롬프트 라이브러리(목록·삽입·저장)
  // ── 훅 조합(의존 순서: core → 독립훅 → workspace → message → conversation → projects → prompts) ──
  const core = useChatCore();
  const ui = useChatUI();
  const features = useChatFeatures();
  const [ragAccess, setRagAccess] = useState(() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      return !!u.rag_access || u.role === 'admin';
    } catch (e) {
      return false;
    }
  });
  const effectiveUseRag = ragAccess ? features.useRag : false;
  // 스킬(절차·검증이 붙은 프롬프트)로 시작한 턴인지. 전송 시점에 읽어 서버로 보낸다.
  const skillIdRef = useRef(null);
  const applySkillTools = (tools) => {
    if (tools.includes('web_search')) features.setWebSearch(true);
    if (tools.includes('internal_data')) features.setInternalData(true);
    if (tools.includes('use_rag')) features.setUseRag(true);
    if (tools.includes('cowork') && core.selectedProjectId) core.setCowork(true);
  };
  const attach = useAttachments();
  const notif = useNotifications();
  const limits = useChatLimits();
  const exporter = useChatExport({ setSaveMenu: ui.setSaveMenu });
  const workspace = useWorkspace({
    selectedProjectId: core.selectedProjectId, cowork: core.cowork, setCowork: core.setCowork,
    downloadBlob: exporter.downloadBlob,
    accessRole: core.selectedProjectId ? (core.currentProjectAccessRole || 'viewer') : 'owner',
  });
  const msg = useChatMessage({
    conversations: core.conversations, setConversations: core.setConversations,
    currentConvId: core.currentConvId, setCurrentConvId: core.setCurrentConvId,
    messages: core.messages, setMessages: core.setMessages, currentConvIdRef: core.currentConvIdRef,
    selectedProjectId: core.selectedProjectId, currentProjectAccessRole: core.currentProjectAccessRole, cowork: core.cowork,
    webSearch: features.webSearch, reasoning: features.reasoning, selectedChatModel: features.selectedChatModel,
    internalData: features.internalData, useRag: effectiveUseRag,
    setRagSources: features.setRagSources, refreshMyUsage: features.refreshMyUsage,
    pendingFiles: attach.pendingFiles, setPendingFiles: attach.setPendingFiles,
    pendingFolders: attach.pendingFolders, setPendingFolders: attach.setPendingFolders,
    loadWorkspaceFiles: workspace.loadWorkspaceFiles, setWorkspaceBusy: workspace.setWorkspaceBusy,
    setLimitModal: limits.setLimitModal, setLimitReason: limits.setLimitReason, setLimitRequested: limits.setLimitRequested,
    setWebLimitModal: limits.setWebLimitModal, setWebLimitRequested: limits.setWebLimitRequested,
    atBottomRef: ui.atBottomRef, setShowScrollDown: ui.setShowScrollDown, scrollRef: ui.scrollRef,
    setChatError, skillIdRef,
  });
  const convH = useConversation({
    setConversations: core.setConversations, currentConvId: core.currentConvId,
    setCurrentConvId: core.setCurrentConvId, setMessages: core.setMessages,
    setSelectedProjectId: core.setSelectedProjectId, setCurrentProjectAccessRole: core.setCurrentProjectAccessRole,
    resetWorkspaceForProject: workspace.resetWorkspaceForProject,
    // 대화를 옮기면 입력창 첨부를 비운다 — 드라이브 폴더 칩도 파일 칩과 함께.
    detachStream: msg.detachStream, setPendingFiles: (v) => { attach.setPendingFiles(v); attach.setPendingFolders?.([]); },
    setRagSources: features.setRagSources,
    atBottomRef: ui.atBottomRef, setShowScrollDown: ui.setShowScrollDown, setConvDrawerOpen: ui.setConvDrawerOpen,
  });
  const projH = useProjects({
    selectedProjectId: core.selectedProjectId, setSelectedProjectId: core.setSelectedProjectId,
    cowork: core.cowork, setCowork: core.setCowork,
    startNew: convH.startNew, setConversations: core.setConversations,
    loadWorkspaceFiles: workspace.loadWorkspaceFiles, setWorkspaceFiles: workspace.setWorkspaceFiles,
  });
  const prompts = usePrompts({ input: msg.input, setInput: msg.setInput, textareaRef: msg.textareaRef,
                               applySkillTools, skillIdRef });

  useEffect(() => {
    if (core.selectedProjectId) workspace.loadWorkspaceFiles(core.selectedProjectId);
    else workspace.setWorkspaceFiles([]);
  }, [core.selectedProjectId]);

  // ── 구조분해(원본 JSX와 동일 변수명 — 화면 본문은 그대로 유지) ──
  const { conversations, currentConvId, messages, selectedProjectId } = core;
  const { mobile, listWidth, setListWidth, convDrawerOpen, setConvDrawerOpen, showArtifacts, setShowArtifacts,
          saveMenu, setSaveMenu, projectsOpen, setProjectsOpen, showScrollDown, scrollToBottom,
          scrollRef, bottomRef, handleMessagesScroll, keyboardOpen } = ui;
  const { webSearch, setWebSearch, reasoning, setReasoning, internalData, setInternalData, useRag, setUseRag, ragSources,
          dbAccess, internalLan, driveAccess, models, selectedChatModel, setSelectedChatModel,
          showDrivePicker, setShowDrivePicker } = features;
  const { pendingFiles, setPendingFiles, dragOver, setDragOver, fileRef, MAX_FILES,
          uploadFiles, handleFileChange, handlePaste, downloadFile,
          pendingFolders = [], addDriveFolder, removeDriveFolder } = attach;
  const { notifications, dismissNotification } = notif;
  const { limitModal, setLimitModal, webLimitModal, setWebLimitModal, limitReason, setLimitReason,
          limitRequested, webLimitRequested, handleLimitRequest, handleWebLimitRequest } = limits;
  const { renderMessage, saveMsg, exportTxt, exportPdf } = exporter;
  const { workspaceFiles, workspaceBusy, wsFileRef, toggleCowork,
          uploadWorkspaceFile, downloadWorkspaceFile, deleteWorkspaceFile,
          canWorkspaceRead, canWorkspaceDownload, canWorkspaceUpload, canWorkspaceDelete } = workspace;
  const { input, setInput, textareaRef, loading, generatingDoc, genConvIds,
          stopResponse, handleSend, handleRegenerate, handleEditMessage, handleDeleteMessage } = msg;
  const { favOnly, setFavOnly, archiveView, setArchiveView, tagFilter, setTagFilter,
          searchQuery, searchResults, editingTitleId, setEditingTitleId, editingTitleVal, setEditingTitleVal,
          loadConversation, saveTitle, startNew, handleDeleteConv, toggleFavorite, toggleArchive, editTags, handleSearch } = convH;
  const { projects, projModal, setProjModal, projFiles, showNewProj, setShowNewProj, newProjName, setNewProjName,
          selectProject, createProject, openProjSettings, saveProjSettings, deleteProject, uploadProjFile, deleteProjFile } = projH;
  const { showPrompts, setShowPrompts, promptList, promptQ, setPromptQ, openPrompts, insertPrompt, saveMyPrompt } = prompts;

  useEffect(() => {
    if (!mobile) return undefined;
    const bodyStyle = document.body.style;
    const previousOverflow = bodyStyle.getPropertyValue('overflow');
    const previousPriority = bodyStyle.getPropertyPriority('overflow');
    bodyStyle.setProperty('overflow', 'hidden');

    return () => {
      if (previousOverflow) {
        bodyStyle.setProperty('overflow', previousOverflow, previousPriority);
      } else {
        bodyStyle.removeProperty('overflow');
      }
    };
  }, [mobile]);

  const selectedProject = selectedProjectId
    ? projects.find((p) => String(p.id) === String(selectedProjectId))
    : null;
  const selectedProjectRole = core.currentProjectAccessRole
    || selectedProject?.access_role
    || selectedProject?.role
    || (selectedProject ? (selectedProject.shared ? 'viewer' : 'owner') : (selectedProjectId ? 'viewer' : 'owner'));

  useEffect(() => {
    if (!selectedProjectId) {
      core.setCurrentProjectAccessRole(null);
      return;
    }
    if (!selectedProject) return;
    core.setCurrentProjectAccessRole(
      selectedProject.access_role || selectedProject.role || (selectedProject.shared ? 'viewer' : 'owner'),
    );
  }, [selectedProjectId, selectedProject, core.setCurrentProjectAccessRole]);

  useEffect(() => {
    authAPI.getMe().then((r) => {
      const me = r.data || {};
      const canRag = !!me.rag_access || me.role === 'admin';
      setRagAccess(canRag);
      if (!canRag) setUseRag(false);
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        const merged = { ...u, rag_access: !!me.rag_access, ui_font: me.ui_font };
        if (me.role) merged.role = me.role;
        localStorage.setItem('user', JSON.stringify(merged));
        // 🔴 merged 가 아니라 **서버 응답 me** 를 넘긴다(AppSidebar 와 동일 이유).
        //    merged 는 stale localStorage 를 밑에 깔아서 서버값을 덮어쓴다.
        syncFontFromUser(me);
      } catch (e) { /* */ }
    }).catch(() => {});
  }, [setUseRag]);

  useEffect(() => {
    if (!ragAccess && useRag) setUseRag(false);
  }, [ragAccess, useRag, setUseRag]);

  useEffect(() => {
    const preventFileDrop = (e) => {
      if (e.dataTransfer?.types?.includes('Files')) e.preventDefault();
    };
    window.addEventListener('dragover', preventFileDrop);
    window.addEventListener('drop', preventFileDrop);
    return () => {
      window.removeEventListener('dragover', preventFileDrop);
      window.removeEventListener('drop', preventFileDrop);
    };
  }, []);

  const isFileDrag = (e) => e.dataTransfer?.types?.includes('Files');
  const chatMaxWidth = '900px';
  const chatPadX = mobile ? '12px' : '20px';
  const emptyChat = messages.length === 0;
  const centerEmptyChat = emptyChat && !selectedProjectId;
  const starterPrompts = ['이 문서 요약해줘', '회의록 정리', '코드 리뷰', '이메일 초안'];
  const userName = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      return String(u.name || '').trim();
    } catch (e) {
      return '';
    }
  })();
  const greetingBase = (() => {
    const hour = new Date().getHours();
    if (hour < 12) return '좋은 아침이에요';
    if (hour < 18) return '좋은 오후예요';
    return '좋은 저녁이에요';
  })();
  const emptyGreeting = userName ? `${greetingBase}, ${userName}` : greetingBase;
  useEffect(() => {
    const raw = new URLSearchParams(window.location.search).get('conv');
    if (!raw || convParamRef.current === raw) return;
    convParamRef.current = raw;
    const convId = Number(raw);
    if (!Number.isInteger(convId) || convId <= 0) return;
    loadConversation(convId, { syncUrl: false }).catch(() => {});
  }, [loadConversation]);

  useEffect(() => {
    const raw = new URLSearchParams(window.location.search).get('project');
    if (!raw || projectParamRef.current === raw) return;
    projectParamRef.current = raw;
    const pid = Number(raw);
    if (!Number.isInteger(pid) || pid <= 0) return;
    core.setSelectedProjectId(pid);
  }, [core.setSelectedProjectId]);

  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const convRaw = params.get('conv');
      if (convRaw) {
        convParamRef.current = convRaw;
        const convId = Number(convRaw);
        if (Number.isInteger(convId) && convId > 0) loadConversation(convId, { syncUrl: false }).catch(() => {});
        return;
      }
      convParamRef.current = '';
      startNew({ syncUrl: false });
      const projectRaw = params.get('project');
      if (!projectRaw) {
        core.setSelectedProjectId(null);
        return;
      }
      const pid = Number(projectRaw);
      if (Number.isInteger(pid) && pid > 0) core.setSelectedProjectId(pid);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [loadConversation, startNew, core.setSelectedProjectId]);

  const handleStarterPrompt = (text) => {
    setInput(text);
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, mobile ? 112 : 126) + 'px';
    });
  };

  // ── 렌더 ──
  return (
    // MobileScale의 visualViewport resize 보정값을 따라 키보드가 연 가시 높이에 맞춘다(e2e 회귀 검증).
    <AppShell app="chat" ownTabbarPad style={{
      '--bg': 'var(--gw-bg, #fff)',
      '--surface': 'var(--gw-surface, #fff)',
      '--text': 'var(--gw-text, #32312e)',
      '--text-muted': 'var(--gw-secondary, #64635f)',
      '--border': 'var(--gw-border, #e5e5e2)',
      '--user-bg': 'var(--gw-gray-soft, #f1f1ef)',
      '--accent': COLORS.primary,
      '--accent-dark': COLORS.primaryDark,
      '--accent-weak': COLORS.primarySoft,
      '--accent-border': COLORS.primaryBorder,
      '--selected-text': COLORS.selectedText,
      fontFamily: 'var(--gw-font)',
      background: 'var(--bg)',
      color: 'var(--text)',
    }}>
      <style>{`@keyframes docspin{to{transform:rotate(360deg)}}.chat-message-row .chat-message-time{opacity:0;transition:opacity .15s ease}.chat-message-row:hover .chat-message-time,.chat-message-row:focus-within .chat-message-time{opacity:1}.chat-message-actions{opacity:0;max-height:0;overflow:hidden;pointer-events:none;transform:translateY(-2px);transition:opacity .15s ease,transform .15s ease,max-height .15s ease,margin-top .15s ease;margin-top:0!important}.chat-message-row:hover .chat-message-actions,.chat-message-row:focus-within .chat-message-actions,.chat-message-actions-visible{opacity:1;max-height:48px;overflow:visible;pointer-events:auto;transform:none;margin-top:8px!important}@media (hover:none){.chat-message-row .chat-message-time{opacity:1}.chat-message-actions{opacity:1;max-height:48px;overflow:visible;pointer-events:auto;transform:none;margin-top:8px!important}}`}</style>

      {/* ── 사이드바(공유 AppSidebar — 앱전환 nav·사용량·계정메뉴 공통) ── */}
      <div style={{ flex: 1, display: 'flex', minWidth: 0, minHeight: 0 }}>
        {/* 대화목록 — 데스크톱=좌측 컬럼 / 모바일=off-canvas 드로어(헤더 ☰로 열기, 선택 시 닫힘) */}
        <ConversationList
          mobile={mobile} convDrawerOpen={convDrawerOpen} setConvDrawerOpen={setConvDrawerOpen}
          listWidth={listWidth} setListWidth={setListWidth}
          startNew={startNew} selectProject={selectProject} selectedProjectId={selectedProjectId}
          projectsOpen={projectsOpen} setProjectsOpen={setProjectsOpen} projects={projects} openProjSettings={openProjSettings}
          setShowNewProj={setShowNewProj} setNewProjName={setNewProjName} setShowArtifacts={setShowArtifacts}
          searchQuery={searchQuery} handleSearch={handleSearch} searchResults={searchResults}
          favOnly={favOnly} setFavOnly={setFavOnly} archiveView={archiveView} setArchiveView={setArchiveView} tagFilter={tagFilter} setTagFilter={setTagFilter}
          conversations={conversations} currentConvId={currentConvId} genConvIds={genConvIds}
          toggleFavorite={toggleFavorite} editTags={editTags} toggleArchive={toggleArchive} handleDeleteConv={handleDeleteConv} loadConversation={loadConversation}
          editingTitleId={editingTitleId} setEditingTitleId={setEditingTitleId} editingTitleVal={editingTitleVal} setEditingTitleVal={setEditingTitleVal} saveTitle={saveTitle}
        />

      {/* ── 채팅 영역 ── (모바일: 하단 고정 탭바 52px+safe-area 만큼 여백 확보해 입력창이 안 가려지게) */}
      <div
        onDragEnter={(e) => {
          if (!isFileDrag(e)) return;
          e.preventDefault();
          dragDepth.current++;
          setDragOver(true);
        }}
        onDragOver={(e) => {
          if (isFileDrag(e)) e.preventDefault();
        }}
        onDragLeave={(e) => {
          if (!isFileDrag(e)) return;
          dragDepth.current = Math.max(0, dragDepth.current - 1);
          if (dragDepth.current === 0) setDragOver(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          dragDepth.current = 0;
          setDragOver(false);
          const fs = Array.from(e.dataTransfer?.files || []);
          if (fs.length) uploadFiles(fs);
        }}
        style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0, minHeight: 0, position: 'relative', paddingBottom: mobile ? (keyboardOpen ? undefined : `max(${tabbarCalc(2)}, var(--gw-push-banner-clear, 0px))`) : 'var(--gw-push-banner-clear, 0px)' }}
      >
        {dragOver && (
          <div style={{ position: 'absolute', inset: 0, zIndex: 50, pointerEvents: 'none',
            background: 'var(--gw-blue-soft, #eaf3fc)', border: '1px dashed var(--accent)', borderRadius: 16,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--accent)' }}>파일</div>
            <div style={{ fontWeight: 700, color: 'var(--accent)' }}>파일을 여기에 놓아 첨부하십시오</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>최대 {MAX_FILES}개 · 이미지 5MB · 문서 20MB</div>
          </div>
        )}
        {/* 공지사항 배너 (기능별: AI 채팅) — 맨 위로 */}
        <Announcements scope="chat" />
        {/* 헤더 */}
        <div style={{ background: 'rgba(255,255,255,.92)', borderBottom: '1px solid var(--border)', padding: mobile ? '10px 0' : '12px 0' }}>
          <div style={{ maxWidth: chatMaxWidth, margin: '0 auto', padding: `0 ${chatPadX}`, display: 'flex', alignItems: 'center', gap: '12px', flexWrap: mobile ? 'wrap' : 'nowrap' }}>
          {mobile && (
            <button onClick={() => setConvDrawerOpen(true)} title="대화 목록" aria-label="대화 목록"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center', color: 'var(--gw-text, #32312e)', flexShrink: 0 }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
            </button>
          )}
          <div style={{ width: '28px', height: '28px', background: 'var(--accent)', borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <IconSparkles size={17} stroke={2.2} color="white" aria-hidden="true" />
          </div>
          <div style={{ minWidth: 0, flex: '1 1 auto' }}>
            <div style={{ fontWeight: '700', fontSize: '15px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>아워박스 AI 게이트웨이</div>
            <div style={{ fontSize: '12px', color: 'var(--gw-muted, #92918d)' }}>Claude AI 기반</div>
          </div>
          {messages.length > 0 && (
            <div style={{ marginLeft: mobile ? 0 : 'auto', display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end', width: mobile ? '100%' : undefined }}>
              <button onClick={() => exportTxt(messages)}
                style={{ padding: '6px 11px', background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '500' }}>
                TXT
              </button>
              <button onClick={() => exportPdf(messages)}
                style={{ padding: '6px 11px', background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '500' }}>
                PDF
              </button>
            </div>
          )}
          </div>
        </div>

        <div style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: centerEmptyChat ? 'center' : 'flex-start',
          overflow: emptyChat ? 'auto' : 'hidden',
          padding: emptyChat ? (mobile ? '18px 0 calc(20px + env(safe-area-inset-bottom))' : '24px 0 34px') : undefined,
        }}>
        {/* 메시지 영역 — pages/chat/MessageList */}
        <MessageList
          mobile={mobile}
          keyboardOpen={keyboardOpen}
          messages={messages} loading={loading} currentConvId={currentConvId}
          scrollRef={scrollRef} handleMessagesScroll={handleMessagesScroll} bottomRef={bottomRef}
          downloadFile={downloadFile} renderMessage={renderMessage}
          saveMenu={saveMenu} setSaveMenu={setSaveMenu} saveMsg={saveMsg} handleRegenerate={handleRegenerate}
          handleEditMessage={handleEditMessage} handleDeleteMessage={handleDeleteMessage}
          ragSources={ragSources} genConvIds={genConvIds} generatingDoc={generatingDoc} workspaceBusy={workspaceBusy}
          showScrollDown={showScrollDown} scrollToBottom={scrollToBottom}
          emptyCentered={centerEmptyChat} emptyGreeting={emptyGreeting}
          starterPrompts={starterPrompts} onStarterPromptClick={handleStarterPrompt}
        />

        {selectedProjectId && (
          <div style={{ flex: '0 0 auto', padding: mobile ? '0 0 10px' : '0 0 12px' }}>
            <div style={{ maxWidth: chatMaxWidth, margin: '0 auto', padding: `0 ${chatPadX}`, boxSizing: 'border-box' }}>
              <WorkspacePanel
                mobile={mobile}
                selectedProjectId={selectedProjectId}
                projectName={selectedProject?.name || '선택한 프로젝트'}
                accessRole={selectedProjectRole}
                cowork={core.cowork}
                toggleCowork={toggleCowork}
                workspaceFiles={workspaceFiles}
                workspaceBusy={workspaceBusy}
                wsFileRef={wsFileRef}
                canRead={canWorkspaceRead}
                canDownload={canWorkspaceDownload}
                canUpload={canWorkspaceUpload}
                canDelete={canWorkspaceDelete}
                uploadWorkspaceFile={uploadWorkspaceFile}
                downloadWorkspaceFile={downloadWorkspaceFile}
                deleteWorkspaceFile={deleteWorkspaceFile}
              />
            </div>
          </div>
        )}

        {chatError && (
          <div role="alert" style={{ padding: mobile ? '8px 0 0' : '10px 0 0' }}>
            <div style={{ maxWidth: chatMaxWidth, margin: '0 auto', padding: `0 ${chatPadX}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: '10px', padding: '10px 12px', fontSize: '13px', fontWeight: 600 }}>
              <IconAlertTriangle size={16} stroke={2} aria-hidden="true" style={{ flexShrink: 0 }} />
              <span style={{ flex: 1, minWidth: 0 }}>{chatError}</span>
              {/* 🔴 409(RUN_IN_PROGRESS) 는 문구만 띄우면 빠져나갈 길이 없다. 백그라운드 생성은
                  연결이 끊겨도 계속 돌아서 그 대화가 최대 20분 잠긴다(dev 실측 56초).
                  중지시키고 다시 보내는 것을 한 번에 해준다. */}
              {msg.blockedConvId != null && (
                <button
                  type="button"
                  onClick={msg.stopBlockingRunAndRetry}
                  style={{ flexShrink: 0, border: '1px solid #991b1b', background: 'white', color: '#991b1b',
                           cursor: 'pointer', fontSize: '12px', fontWeight: 700, borderRadius: '8px', padding: '5px 10px' }}
                >
                  중지하고 새로 묻기
                </button>
              )}
              <button
                type="button"
                onClick={() => setChatError(null)}
                aria-label="오류 메시지 닫기"
                style={{ flexShrink: 0, border: 'none', background: 'transparent', color: '#991b1b', cursor: 'pointer', fontSize: '18px', lineHeight: 1, padding: '0 2px' }}
              >
                ×
              </button>
            </div>
            </div>
          </div>
        )}

        {/* 입력 영역 — pages/chat/ChatInput */}
        <ChatInput
          mobile={mobile}
          keyboardOpen={keyboardOpen}
          scrollToBottom={scrollToBottom}
          pendingFiles={pendingFiles} setPendingFiles={setPendingFiles} MAX_FILES={MAX_FILES}
          pendingFolders={pendingFolders} removeDriveFolder={removeDriveFolder}
          textareaRef={textareaRef} input={input} setInput={setInput} handleSend={handleSend} handlePaste={handlePaste}
          fileRef={fileRef} handleFileChange={handleFileChange} driveAccess={driveAccess} setShowDrivePicker={setShowDrivePicker} openPrompts={openPrompts}
          models={models} selectedChatModel={selectedChatModel} setSelectedChatModel={setSelectedChatModel}
          webSearch={webSearch} setWebSearch={setWebSearch}
          reasoning={reasoning} setReasoning={setReasoning}
          dbAccess={dbAccess} internalLan={internalLan} internalData={internalData} setInternalData={setInternalData}
          useRag={effectiveUseRag} setUseRag={setUseRag} ragAccess={ragAccess}
          loading={loading} stopResponse={stopResponse} draftKey={currentConvId || 'new'}
        />
        </div>
      </div>
      </div>{/* /flex 래퍼(대화목록 컬럼 + 채팅영역) */}

      {/* ── 채팅 오버레이 모달 모음 — pages/chat/ChatModals ── */}
      <ChatModals
          showDrivePicker={showDrivePicker} setShowDrivePicker={setShowDrivePicker} uploadFiles={uploadFiles} addDriveFolder={addDriveFolder} showPrompts={showPrompts} setShowPrompts={setShowPrompts} saveMyPrompt={saveMyPrompt}
          promptQ={promptQ} setPromptQ={setPromptQ} promptList={promptList} insertPrompt={insertPrompt} notifications={notifications} dismissNotification={dismissNotification}
          showArtifacts={showArtifacts} setShowArtifacts={setShowArtifacts} messages={messages} saveMsg={saveMsg} showNewProj={showNewProj} setShowNewProj={setShowNewProj}
          newProjName={newProjName} setNewProjName={setNewProjName} createProject={createProject} projModal={projModal} setProjModal={setProjModal} uploadProjFile={uploadProjFile}
          projFiles={projFiles} deleteProjFile={deleteProjFile} saveProjSettings={saveProjSettings} deleteProject={deleteProject} webLimitModal={webLimitModal} setWebLimitModal={setWebLimitModal}
          setWebSearch={setWebSearch} webLimitRequested={webLimitRequested} handleWebLimitRequest={handleWebLimitRequest} limitModal={limitModal} setLimitModal={setLimitModal} limitRequested={limitRequested}
          limitReason={limitReason} setLimitReason={setLimitReason} handleLimitRequest={handleLimitRequest}
      />
    </AppShell>
  );
}
